import { useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components";

import PropertyPane from "./PropertyPane";

import apiAxios from "../helpers/apiAxios";

import { AxiosError } from "axios";

import { IProperty } from "../helpers/commonInterfaces";

const PROPERTIES_PER_PAGE = 6;

const OurPropertiesSection = () => {
  const [properties, setProperties] = useState<IProperty[] | null>(null);

  const [visiblePropertyCount, setVisiblePropertyCount] =
    useState(PROPERTIES_PER_PAGE);

  const infiniteScrollTriggerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    apiAxios.get("/website/properties").then(
      (axiosResponse) => setProperties(axiosResponse.data.properties),

      (
        axiosError: AxiosError<{
          errorCode: string;
        }>,
      ) => {
        if (axiosError.response?.data.errorCode)
          alert(
            `Unable to process your request.\n\n${axiosError.response.data.errorCode}.\n\nPlease try again.`,
          );

        alert(
          `Unable to process your request.\n\n${axiosError.message}.\n\nPlease try again.`,
        );
      },
    );
  }, []);

  const visibleProperties = useMemo(
    () => properties?.slice(0, visiblePropertyCount) || [],
    [properties, visiblePropertyCount],
  );

  const hasMoreProperties =
    properties && visiblePropertyCount < properties.length;

  useEffect(() => {
    if (!hasMoreProperties) return;

    const infiniteScrollTrigger = infiniteScrollTriggerRef.current;

    if (!infiniteScrollTrigger) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        observer.disconnect();

        setVisiblePropertyCount((currentCount) =>
          Math.min(
            currentCount + PROPERTIES_PER_PAGE,
            properties?.length || currentCount,
          ),
        );
      },
      {
        rootMargin: "300px",
      },
    );

    observer.observe(infiniteScrollTrigger);

    return () => observer.disconnect();
  }, [hasMoreProperties, properties?.length, visiblePropertyCount]);

  return (
    <Container>
      <Title>Our Properties</Title>

      <ContentContainer>
        <PropertiesContainer>
          {visibleProperties.map((property, index) => (
            <PropertyPane key={index} property={property} />
          ))}
        </PropertiesContainer>

        {hasMoreProperties && (
          <InfiniteScrollTrigger ref={infiniteScrollTriggerRef} />
        )}

        <Footer>
          Call 602-262-9464 if you have any questions about our available
          wholesale properties
        </Footer>
      </ContentContainer>
    </Container>
  );
};

export default OurPropertiesSection;

const Container = styled.div`
  max-width: 1600px;

  margin-left: auto;

  margin-right: auto;

  padding: 1rem;

  display: grid;
`;

const Title = styled.div`
  color: #283878;

  font-size: 2rem;

  text-align: center;

  @media (min-width: 800px) {
    font-size: 3rem;
  }
`;

const ContentContainer = styled.div`
  padding: 1rem;

  display: grid;

  gap: 1rem;
`;

const PropertiesContainer = styled.div`
  display: grid;

  gap: 1rem;

  @media (min-width: 700px) {
    display: grid;

    grid-template-columns: 0.3fr 0.3fr 0.3fr;

    gap: 1rem;

    justify-content: space-evenly;
  }
`;

const Footer = styled.div`
  font-size: 0.9rem;

  text-align: center;
`;

const InfiniteScrollTrigger = styled.div`
  height: 1px;
`;
